import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import { DS } from './ds';
const DELAY_MS = 40000; // 40 segundos
const STORAGE_KEY = 'alinova_promo_dismissed';
export const PromoModal = () => {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // No mostrar si ya fue cerrado en esta sesión
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem(STORAGE_KEY, '1');
    setTimeout(() => setMounted(false), 400);
  };

  // Cerrar con Escape
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [visible]);
  if (!mounted) return null;
  return <>
      {/* Overlay */}
      <div role="presentation" onClick={dismiss} style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: 'rgba(11,31,36,0.55)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      opacity: visible ? 1 : 0,
      transition: `opacity 0.35s ${DS.ease.smooth}`
    }} data-id="element-95" data-mp="2f89gyhbitr3j" />

      {/* Modal */}
      <div role="dialog" aria-modal="true" aria-labelledby="promo-title" style={{
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      pointerEvents: 'none'
    }} data-id="element-96" data-mp="1ig5ynoasizcr">
        <div style={{
        width: '100%',
        maxWidth: '480px',
        borderRadius: '28px',
        overflow: 'hidden',
        boxShadow: '0 40px 120px rgba(0,0,0,0.28)',
        pointerEvents: 'auto',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
        transition: `opacity 0.4s ${DS.ease.out}, transform 0.4s ${DS.ease.out}`
      }} data-id="element-97" data-mp="3hsier4yysz53">
          {/* Franja superior teal */}
          <div style={{
          background: `linear-gradient(135deg, ${DS.color.tealDeep} 0%, #07202a 100%)`,
          padding: '36px 36px 32px',
          position: 'relative',
          overflow: 'hidden'
        }} data-id="element-98" data-mp="3u3j5ci4qlr7m">
            {/* Grid CAD sutil */}
            <div aria-hidden="true" style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(75,199,210,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(75,199,210,0.05) 1px, transparent 1px)',
            backgroundSize: '36px 36px',
            pointerEvents: 'none'
          }} data-id="element-99" data-mp="lnpn9yurc7og" />

            {/* Glow */}
            <div aria-hidden="true" style={{
            position: 'absolute',
            top: '-40px',
            right: '-40px',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(ellipse, rgba(75,199,210,0.12) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} data-id="element-100" data-mp="y4qqirngy2kc" />

            {/* Botón cerrar */}
            <button type="button" onClick={dismiss} aria-label="Cerrar" style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: `background 0.2s ${DS.ease.smooth}`,
            zIndex: 2
          }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'} data-id="element-101" data-mp="1lvwe5sznhz57">
              <XIcon size={15} color="rgba(255,255,255,0.8)" data-id="element-102" data-mp="2il6ofwx2gib3" />
            </button>

            {/* Badge */}
            <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            padding: '5px 14px',
            borderRadius: '100px',
            background: 'rgba(75,199,210,0.12)',
            border: '1px solid rgba(75,199,210,0.25)',
            marginBottom: '20px',
            position: 'relative',
            zIndex: 1
          }} data-id="element-103" data-mp="2dqj7qk8b0s72">
              <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: DS.color.aqua,
              display: 'block',
              animation: 'waPulse 2s ease-out infinite'
            }} data-id="element-104" data-mp="3nnr43am49inj" />
              <span style={{
              fontFamily: DS.font.body,
              fontSize: '11px',
              fontWeight: 700,
              color: DS.color.aquaLight,
              letterSpacing: '0.09em',
              textTransform: 'uppercase'
            }} data-id="element-105" data-mp="p6dcqt7956dc">
                Atención personalizada
              </span>
            </div>

            <h2 id="promo-title" style={{
            fontFamily: DS.font.heading,
            fontWeight: 700,
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            color: '#fff',
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            marginBottom: '12px',
            position: 'relative',
            zIndex: 1
          }} data-id="element-106" data-mp="1hr0nvsofx1vj">
              Agenda tu asesoría{' '}
              <span style={{
              background: `linear-gradient(90deg, ${DS.color.aquaLight}, ${DS.color.aqua})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }} data-id="element-107" data-mp="yu9afzvkxgze">
                sin costo.
              </span>
            </h2>

            <p style={{
            fontFamily: DS.font.body,
            fontSize: '15px',
            color: 'rgba(255,255,255,0.65)',
            lineHeight: 1.6,
            margin: 0,
            position: 'relative',
            zIndex: 1
          }} data-id="element-108" data-mp="g1yaoew81z5i">
              Cuéntanos tu caso y un asesor de Alinova te guía desde el primer paso. Sin compromisos.
            </p>
          </div>

          {/* Parte inferior ivory */}
          <div style={{
          background: DS.color.ivory,
          padding: '28px 36px 32px'
        }} data-id="element-109" data-mp="30q8dqaugeovu">
            {/* Checklist rápido */}
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginBottom: '24px'
          }} data-id="element-110" data-mp="28116mngxoqyz">
              {['Resolución de dudas en minutos', 'Orientación sobre modalidad ideal para tu clínica', 'Sin presión ni compromisos de volumen'].map(item => <div key={item} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }} data-id="element-111" data-mp="1p9i4eiuqhgy0">
                  <span style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: DS.color.mint,
                border: `1px solid ${DS.color.aqua}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }} data-id="element-112" data-mp="dpz3fcc0ad1g">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-113" data-mp="1bb3d837v9ism">
                      <path d="M2 5l2.5 2.5L8 3" stroke={DS.color.teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-114" data-mp="16e4l1q7rjlx2" />
                    </svg>
                  </span>
                  <span style={{
                fontFamily: DS.font.body,
                fontSize: '13px',
                color: DS.color.body
              }} data-id="element-115" data-mp="1yx6zatf5zme8">
                    {item}
                  </span>
                </div>)}
            </div>

            {/* CTA WhatsApp */}
            <a href={DS.wa} target="_blank" rel="noopener noreferrer" onClick={dismiss} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '15px 24px',
            borderRadius: '100px',
            background: `linear-gradient(135deg, ${DS.color.aquaLight} 0%, ${DS.color.aqua} 100%)`,
            boxShadow: '0 8px 26px rgba(75, 199, 210, 0.45)',
            color: DS.color.tealDeep,
            fontFamily: DS.font.body,
            fontSize: '15px',
            fontWeight: 700,
            textDecoration: 'none',
            transition: `box-shadow 0.22s ${DS.ease.smooth}, transform 0.22s ${DS.ease.smooth}`,
            boxSizing: 'border-box'
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 34px rgba(75, 199, 210, 0.58)';
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 26px rgba(75, 199, 210, 0.45)';
          }} data-id="element-116" data-mp="1g80g75agksrw">
              {/* Icono WhatsApp */}
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden="true" data-id="element-117" data-mp="2xfnharo3p1ti">
                <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.61 1.832 6.505L4 29l7.697-1.807A11.94 11.94 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z" fill={DS.color.tealDeep} opacity="0.14" data-id="element-118" data-mp="2xhgt0ldkmc8d" />
                <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.61 1.832 6.505L4 29l7.697-1.807A11.94 11.94 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z" stroke={DS.color.tealDeep} strokeWidth="1.2" fill="none" data-id="element-119" data-mp="3gjqvtz2xdamo" />
                <path d="M22.003 18.837c-.306-.153-1.809-.893-2.089-.994-.28-.1-.484-.153-.688.153-.203.306-.79.994-.968 1.198-.178.203-.356.229-.662.076-.306-.153-1.292-.476-2.461-1.518-.91-.811-1.524-1.813-1.702-2.119-.178-.306-.019-.471.134-.623.137-.137.306-.357.459-.535.153-.178.204-.306.306-.51.102-.203.051-.382-.025-.535-.077-.153-.688-1.66-.942-2.272-.248-.596-.5-.515-.688-.524l-.586-.01c-.203 0-.535.077-.815.382-.28.306-1.07 1.045-1.07 2.549s1.096 2.957 1.248 3.161c.153.203 2.156 3.291 5.226 4.615.73.315 1.3.503 1.744.644.733.233 1.4.2 1.927.121.588-.088 1.809-.74 2.064-1.454.255-.714.255-1.326.178-1.454-.077-.127-.28-.203-.586-.357z" fill={DS.color.tealDeep} data-id="element-120" data-mp="2jvt0yv6tx7fd" />
              </svg>
              Agendar asesoría por WhatsApp
            </a>

            {/* Cerrar sin acción */}
            <button type="button" onClick={dismiss} style={{
            marginTop: '14px',
            width: '100%',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: DS.font.body,
            fontSize: '13px',
            color: DS.color.muted,
            textAlign: 'center',
            padding: '6px',
            transition: `color 0.2s ${DS.ease.smooth}`
          }} onMouseEnter={e => e.currentTarget.style.color = DS.color.ink} onMouseLeave={e => e.currentTarget.style.color = DS.color.muted} data-id="element-121" data-mp="3sn1zer5xt9rl">
              Ahora no, seguir explorando
            </button>
          </div>
        </div>
      </div>
    </>;
};